import { BACKGROUND_COLORS } from 'design-system/colors';
import type { Location } from 'models/api';
import type { ColorString } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	articleNumber?: string;
	categoryColor: ColorString | undefined;
	categoryName: string | undefined;
	className?: string;
	locations: Location[] | undefined;
}

/**
 * Get dark or light text color based on the background color using WCAG 2
 * relative luminance formula.
 */
function textColorForBg(hex: ColorString): string {
	// Strip # and expand 3-digit hex
	let h = hex.replace('#', '');
	if (h.length === 3) {
		// Won't have special characters like emoji here.
		// oxlint-disable-next-line typescript/no-misused-spread
		h = [...h].map((c) => c + c).join('');
	}

	// RGB channels in 0 to 1.
	const r = Number.parseInt(h.slice(0, 2), 16) / 255;
	const g = Number.parseInt(h.slice(2, 4), 16) / 255;
	const b = Number.parseInt(h.slice(4, 6), 16) / 255;

	// sRGB to linear light and get relative luminance (0 black to 1 white).
	const toLinear = (c: number) =>
		c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

	// Threshold ~0.179 is the point where contrast against
	// white and black is equal.
	return L > 0.179 ? 'text-base' : 'text-inverse';
}

function LocationLabel({
	backgroundColor = BACKGROUND_COLORS.inverse,
	text,
}: {
	backgroundColor?: ColorString;
	text: string;
}) {
	return (
		<p
			className={cn(
				'inline-block rounded-xs px-2 py-1 font-bold',
				textColorForBg(backgroundColor),
			)}
			style={{ backgroundColor }}
		>
			{text}
		</p>
	);
}
LocationLabel.displayName = 'ShelfInformation_LocationLabel';

export default function ShelfInformation({
	articleNumber,
	categoryColor,
	categoryName,
	className,
	locations,
}: Props) {
	const { t } = useI18n();

	const primaryLocation = locations?.[0]?.displayName;

	if (!primaryLocation || !categoryName) {
		return null;
	}

	const alternativeLocations = locations.slice(1);

	return (
		<div className={cn(className, 'text-sm')}>
			<p>{t('shelf_information_primary_location_label')}:</p>
			<div className="mt-1 flex items-start gap-2">
				<LocationLabel backgroundColor={categoryColor} text={categoryName} />
				<LocationLabel text={primaryLocation} />
			</div>
			{is.arrayWithLength(alternativeLocations) && (
				<>
					<p className="mt-3">
						{t('shelf_information_alternative_locations_label')}:
					</p>
					<div className="mt-1 flex flex-wrap gap-2">
						{alternativeLocations.map((location) => (
							<LocationLabel
								key={location.displayName}
								text={location.displayName}
							/>
						))}
					</div>
				</>
			)}
			{articleNumber && (
				<p className="mt-3">
				{t('general_article_number_label')}:{' '}
				<span className="font-bold">{articleNumber}</span>
			</p>
				)}
		</div>
	);
}
ShelfInformation.displayName = 'ShelfInformation';
